import { TypographyComponent as TypographyComponentV2 } from "@gx1/gx-ui-common";
import { ComponentProps, Fragment, ReactNode } from "react";
import { TypographyBoldness, TypographyColor, TypographyElement, TypographyStyling } from "./typography.types";

type TypographyV2Color = ComponentProps<typeof TypographyComponentV2>["color"];
type TypographyV2Styling = ComponentProps<typeof TypographyComponentV2>["styling"];

const TYPOGRAPHY_COLOR_MAP: Record<TypographyColor, TypographyV2Color> = {
  black: "grey9",
  berry: "opal",
  blueBerry: "cobalt",
  carbon: "grey9",
  coal: "grey8",
  darkLemon: "warning-text",
  darkPear: "success-text",
  defaultCta: "interaction-01-default",
  gartnerBlue: "gartner-blue",
  hoverCta: "interaction-02-hover",
  huckleberry: "cobalt",
  inherit: "inherit",
  iron: "grey7",
  lightBerry: "interaction-hover-only",
  lightHuckleberry: "sapphire",
  poisonCherry: "error-text",
  quartz: "grey2",
  silver: "grey4",
  skyBrand: "aquamarine",
  slate: "grey6",
  statusCherry: "error-graphic",
  stone: "grey5",
  sweetHuckleberry: "gartner-blue",
  tangerine: "gartner-blue",
  white: "white",
  yam: "grey6",
};

// TODO inaccurate mapping, update once typography styles in gx-ui-common are implemented
const TYPOGRAPHY_STYLING_MAP: Record<TypographyStyling, TypographyV2Styling | ""> = {
  h1: "h2-xl",
  h2: "h3-xl",
  h3: "h2",
  h4: "h3",
  h5: "h4",
  p1: "p-lg",
  p2: "p-default",
  p3: "p-sm",
  p4: "p-xs",
  p5: "p-xs",
  span: "",
};

const TYPOGRAPHY_ELEMENT_MAP: Record<string, TypographyElement> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  p: "p",
  p1: "p",
  p2: "p",
  p3: "p",
  p4: "p",
  p5: "p",
  "p-default": "p",
  "p-xl": "p",
  "p-lg": "p",
  "p-sm": "p",
  "p-xs": "p",
  span: "span",
};

type TypographyProps = {
  boldness?: TypographyBoldness;
  children: ReactNode[] | ReactNode | number | string;
  color?: TypographyColor;
  /** overrides the element defaulted by the "styling" prop - can be: h1-5, p, or span */
  element?: TypographyElement;
  rootClassName?: string;
  /** style to set by design - can be: h1-5, p1-5, or span */
  styling?: TypographyStyling;
  isMasked?: boolean;
  /** @deprecated use "styling" prop instead */
  type?: TypographyStyling | "";
};

/**
 * @deprecated DO NOT USE, DO NOT UPDATE
 */
export const TypographyComponent = ({
  boldness,
  children,
  color = "carbon",
  element,
  rootClassName = "",
  styling = "span",
  type = "",
  isMasked = false,
}: TypographyProps) => {
  const style = type || styling;
  const bold = ["h1", "h2", "h3", "h4", "h5"].includes(style) ? "semi" : boldness;
  return (
    <Fragment>
      <TypographyComponentV2
        boldness={bold === "medium" ? "semi" : bold}
        color={TYPOGRAPHY_COLOR_MAP[color]}
        element={TYPOGRAPHY_ELEMENT_MAP[element || style]}
        isMasked={isMasked}
        rootClassName={rootClassName}
        styling={TYPOGRAPHY_STYLING_MAP[style] as TypographyV2Styling}
      >{children}</TypographyComponentV2>
    </Fragment>
  );
};
